import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Upload, FileText, Palette, Sparkles, Trash2, RefreshCw, Lock, Unlock,
  Loader2, ChevronDown, ChevronRight, AlertCircle, CheckCircle2, X,
} from 'lucide-react';
import { AppHeader } from '@/components/AppHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
  fetchBrandProfile, patchBrandProfile, triggerDistill,
  fetchBrandAssets, uploadBrandAsset, deleteBrandAsset, reprocessBrandAsset,
  ASSET_TYPE_LABELS,
  type BrandProfile, type BrandAsset, type BrandAssetType, type BrandAssetStatus,
} from '@/lib/brand';

// ── Voice descriptor presets ────────────────────────────────────────────────
const VOICE_PRESETS = [
  'warm','confident','playful','professional','bold','minimal',
  'friendly','authoritative','casual','elegant','witty','sincere',
];

const CTA_PRESETS = [
  'Shop Now','Learn More','Get Started','Try Free','Book a Demo',
  'Subscribe','Discover','Explore','Join Us','Claim Your Offer',
];

// Morandi-inspired color palettes — muted, elegant tones
const COLOR_PALETTES: Array<{ name: string; primary: string; accent: string }> = [
  { name: 'Rose Dusk',     primary: '#c4a4a0', accent: '#8b6f6a' },
  { name: 'Sage Mist',     primary: '#a3b5a6', accent: '#6b7f6d' },
  { name: 'Warm Sand',     primary: '#c8b99a', accent: '#8a7d63' },
  { name: 'Steel Blue',    primary: '#9aafc1', accent: '#5d7a94' },
  { name: 'Lavender Haze', primary: '#b5a8c9', accent: '#7e6f98' },
  { name: 'Terracotta',    primary: '#c69683', accent: '#96604d' },
  { name: 'Ink & Cream',   primary: '#f5f0e8', accent: '#2c2c2c' },
  { name: 'Coral Blush',   primary: '#e8a598', accent: '#c25d4e' },
];

// ── Questionnaire ───────────────────────────────────────────────────────────
function BrandQuestionnaire({ onComplete }: { onComplete: (p: BrandProfile) => void }) {
  const [step, setStep] = useState(0);
  const [name, setName] = useState('');
  const [oneLiner, setOneLiner] = useState('');
  const [audience, setAudience] = useState('');
  const [voice, setVoice] = useState<string[]>([]);
  const [customVoice, setCustomVoice] = useState('');
  const [primary, setPrimary] = useState('#c4a4a0');
  const [accent, setAccent] = useState('#8b6f6a');
  const [ctaStyle, setCtaStyle] = useState('');
  const [doSay, setDoSay] = useState('');
  const [dontSay, setDontSay] = useState('');
  const [slogans, setSlogans] = useState('');
  const [saving, setSaving] = useState(false);

  const steps = [
    // Step 0: Brand name
    <div key="name" className="space-y-4">
      <Label className="text-sm font-medium">What's your brand called?</Label>
      <Input value={name} onChange={e => setName(e.target.value)} placeholder="Acme Co." autoFocus />
      <Label className="text-sm font-medium mt-4">One-liner description</Label>
      <Input value={oneLiner} onChange={e => setOneLiner(e.target.value)} placeholder="Premium skincare for modern women" />
    </div>,
    // Step 1: Audience
    <div key="audience" className="space-y-4">
      <Label className="text-sm font-medium">Who is your target audience?</Label>
      <textarea className="border-input bg-background text-foreground w-full rounded-md border px-3 py-2 text-sm min-h-[80px]" value={audience} onChange={e => setAudience(e.target.value)} placeholder="Health-conscious millennials aged 25-40 who value clean ingredients…" />
    </div>,
    // Step 2: Voice
    <div key="voice" className="space-y-4">
      <Label className="text-sm font-medium">Pick 3-5 words that describe your brand voice</Label>
      <div className="flex flex-wrap gap-2">
        {VOICE_PRESETS.map(v => (
          <button key={v} type="button" onClick={() => setVoice(prev => prev.includes(v) ? prev.filter(x => x !== v) : prev.length < 5 ? [...prev, v] : prev)}
            className={cn('rounded-full border px-3 py-1 text-xs font-medium transition', voice.includes(v) ? 'border-primary bg-accent text-primary' : 'border-border text-muted-foreground hover:border-foreground/30')}>
            {v}
          </button>
        ))}
      </div>
      <Input value={customVoice} onChange={e => setCustomVoice(e.target.value)} placeholder="Or type your own…" onKeyDown={e => { if (e.key === 'Enter' && customVoice.trim() && voice.length < 5) { setVoice(p => [...p, customVoice.trim()]); setCustomVoice(''); }}} />
    </div>,
    // Step 3: Colors
    <div key="colors" className="space-y-4">
      <Label className="text-sm font-medium">Pick a color palette</Label>
      <div className="grid grid-cols-2 gap-2">
        {COLOR_PALETTES.map(p => (
          <button
            key={p.name}
            type="button"
            onClick={() => { setPrimary(p.primary); setAccent(p.accent); }}
            className={cn(
              'rounded-lg border p-3 text-left transition',
              primary === p.primary && accent === p.accent
                ? 'border-primary ring-primary/20 ring-2'
                : 'border-border hover:border-foreground/20',
            )}
          >
            <div className="flex items-center gap-2 mb-1.5">
              <div className="size-5 rounded-full border border-border/50 shadow-sm" style={{ background: p.primary }} />
              <div className="size-5 rounded-full border border-border/50 shadow-sm" style={{ background: p.accent }} />
            </div>
            <div className="text-xs font-medium">{p.name}</div>
          </button>
        ))}
      </div>
      <details className="group">
        <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground transition">
          Custom colors ▸
        </summary>
        <div className="flex gap-6 mt-3">
          <div className="space-y-1.5">
            <Label className="text-[10px] text-muted-foreground">Primary</Label>
            <div className="flex items-center gap-2">
              <input type="color" value={primary} onChange={e => setPrimary(e.target.value)} className="h-7 w-7 cursor-pointer rounded border-0" />
              <Input value={primary} onChange={e => setPrimary(e.target.value)} className="w-20 font-mono text-[10px] h-7" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-[10px] text-muted-foreground">Accent</Label>
            <div className="flex items-center gap-2">
              <input type="color" value={accent} onChange={e => setAccent(e.target.value)} className="h-7 w-7 cursor-pointer rounded border-0" />
              <Input value={accent} onChange={e => setAccent(e.target.value)} className="w-20 font-mono text-[10px] h-7" />
            </div>
          </div>
        </div>
      </details>
    </div>,
    // Step 4: CTA
    <div key="cta" className="space-y-4">
      <Label className="text-sm font-medium">What CTA style fits your brand?</Label>
      <div className="flex flex-wrap gap-2">
        {CTA_PRESETS.map(c => (
          <button key={c} type="button" onClick={() => setCtaStyle(c)}
            className={cn('rounded-full border px-3 py-1 text-xs font-medium transition', ctaStyle === c ? 'border-primary bg-accent text-primary' : 'border-border text-muted-foreground hover:border-foreground/30')}>
            {c}
          </button>
        ))}
      </div>
      <Input value={ctaStyle} onChange={e => setCtaStyle(e.target.value)} placeholder="Or describe your CTA style…" />
    </div>,
    // Step 5: Phrases
    <div key="phrases" className="space-y-4">
      <Label className="text-sm font-medium">Brand slogans or signature phrases (optional)</Label>
      <Input value={slogans} onChange={e => setSlogans(e.target.value)} placeholder="Think different / Just do it" />
      <Label className="text-sm font-medium">Words you always use</Label>
      <Input value={doSay} onChange={e => setDoSay(e.target.value)} placeholder="e.g. curated, artisan, conscious" />
      <Label className="text-sm font-medium">Words to avoid</Label>
      <Input value={dontSay} onChange={e => setDontSay(e.target.value)} placeholder="e.g. cheap, discount, basic" />
    </div>,
  ];

  async function handleFinish() {
    setSaving(true);
    try {
      const profile = await patchBrandProfile({
        brand_name: name || null,
        one_liner: oneLiner || null,
        audience_description: audience || null,
        voice_descriptors: voice,
        colors: { primary, secondary: null, accent, neutral: null },
        typical_cta_style: ctaStyle || null,
        do_say: doSay ? doSay.split(',').map(s => s.trim()).filter(Boolean) : [],
        dont_say: dontSay ? dontSay.split(',').map(s => s.trim()).filter(Boolean) : [],
        signature_phrases: slogans ? slogans.split('/').map(s => s.trim()).filter(Boolean) : [],
      });
      onComplete(profile);
    } catch { /* error handled upstream */ }
    setSaving(false);
  }

  return (
    <Card className="mx-auto max-w-lg">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg"><Sparkles className="size-5 text-primary" />Brand Questionnaire</CardTitle>
        <p className="text-muted-foreground text-sm">Step {step + 1} of {steps.length}</p>
        <div className="flex gap-1 mt-2">{steps.map((_, i) => <div key={i} className={cn('h-1 flex-1 rounded-full transition-colors', i <= step ? 'bg-primary' : 'bg-border')} />)}</div>
      </CardHeader>
      <CardContent className="space-y-6">
        {steps[step]}
        <div className="flex justify-between">
          <Button variant="outline" size="sm" onClick={() => setStep(s => s - 1)} disabled={step === 0}>Back</Button>
          {step < steps.length - 1
            ? <Button size="sm" onClick={() => setStep(s => s + 1)}>Next →</Button>
            : <Button size="sm" onClick={handleFinish} disabled={saving}>{saving ? <><Loader2 className="size-3 animate-spin mr-1" />Saving…</> : 'Finish ✓'}</Button>}
        </div>
      </CardContent>
    </Card>
  );
}

// ── Setup choice (empty state) ──────────────────────────────────────────────
function BrandSetupChoice({ onUpload, onQuestionnaire }: { onUpload: () => void; onQuestionnaire: () => void }) {
  return (
    <div className="mx-auto max-w-lg space-y-6 text-center">
      <div>
        <h2 className="text-2xl font-semibold tracking-tight">Set up your brand</h2>
        <p className="text-muted-foreground mt-2 text-sm">Help SendFast learn your brand so every email sounds like you.</p>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <button onClick={onUpload} className="group rounded-xl border border-border p-6 text-left transition hover:border-primary/40 hover:bg-accent/50">
          <Upload className="size-8 text-primary mb-3" />
          <div className="font-medium">Upload Files</div>
          <p className="text-muted-foreground text-xs mt-1 leading-relaxed">Brand guide, past emails, logo, etc.</p>
        </button>
        <button onClick={onQuestionnaire} className="group rounded-xl border border-border p-6 text-left transition hover:border-primary/40 hover:bg-accent/50">
          <FileText className="size-8 text-primary mb-3" />
          <div className="font-medium">Answer Questions</div>
          <p className="text-muted-foreground text-xs mt-1 leading-relaxed">6 quick questions about your brand</p>
        </button>
      </div>
      <p className="text-muted-foreground text-xs">You can always do both later.</p>
    </div>
  );
}

// ── File manager ────────────────────────────────────────────────────────────
function FileManager({ assets, onUpload, onDelete, onReprocess, uploading }: {
  assets: BrandAsset[];
  onUpload: (file: File, type: BrandAssetType) => void;
  onDelete: (id: string) => void;
  onReprocess: (id: string) => void;
  uploading: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [selectedType, setSelectedType] = useState<BrandAssetType>('brand_guidelines');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  function toggle(id: string) {
    setExpanded(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }

  const statusIcon = (s: BrandAssetStatus) => {
    if (s === 'ingested') return <CheckCircle2 className="size-3.5 text-success" />;
    if (s === 'failed') return <AlertCircle className="size-3.5 text-destructive" />;
    return <Loader2 className="size-3.5 animate-spin text-muted-foreground" />;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Select value={selectedType} onValueChange={v => setSelectedType(v as BrandAssetType)}>
          <SelectTrigger className="w-44 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(ASSET_TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
        </Select>
        <input ref={inputRef} type="file" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) onUpload(f, selectedType); e.target.value = ''; }} />
        <Button size="sm" variant="outline" onClick={() => inputRef.current?.click()} disabled={uploading}>
          {uploading ? <Loader2 className="size-3 animate-spin mr-1" /> : <Upload className="size-3 mr-1" />}Upload
        </Button>
      </div>
      {assets.length === 0 && <p className="text-muted-foreground text-xs py-4 text-center">No files uploaded yet.</p>}
      <div className="space-y-2">
        {assets.map(a => (
          <div key={a.id} className="rounded-lg border border-border bg-card">
            <button type="button" onClick={() => toggle(a.id)} className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm">
              {expanded.has(a.id) ? <ChevronDown className="size-3 text-muted-foreground" /> : <ChevronRight className="size-3 text-muted-foreground" />}
              {statusIcon(a.status)}
              <span className="flex-1 truncate font-medium text-xs">{a.original_name}</span>
              <Badge variant="secondary" className="text-[10px]">{ASSET_TYPE_LABELS[a.asset_type]}</Badge>
              <span className="text-muted-foreground text-[10px]">{(a.size_bytes / 1024).toFixed(0)} KB</span>
            </button>
            {expanded.has(a.id) && (
              <div className="border-t border-border px-3 py-2 space-y-2">
                {a.ai_notes && <p className="text-xs text-muted-foreground"><span className="font-medium text-foreground">AI learned:</span> {a.ai_notes}</p>}
                {a.error_message && <p className="text-xs text-destructive">{a.error_message}</p>}
                <div className="flex gap-2">
                  {a.status === 'failed' && <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => onReprocess(a.id)}><RefreshCw className="size-2.5 mr-1" />Retry</Button>}
                  <Button size="sm" variant="ghost" className="h-6 text-[10px] text-destructive hover:text-destructive" onClick={() => onDelete(a.id)}><Trash2 className="size-2.5 mr-1" />Delete</Button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Profile editor ──────────────────────────────────────────────────────────
function ProfileEditor({ profile, onSave, onDistill, distilling, hasIngestedAssets }: {
  profile: BrandProfile;
  onSave: (patches: Partial<BrandProfile>) => Promise<void>;
  onDistill: () => void;
  distilling: boolean;
  hasIngestedAssets: boolean;
}) {
  const [editing, setEditing] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const locked = new Set(profile.manual_overrides);

  function startEdit(field: string, value: string) { setEditing(field); setEditValue(value); }
  async function saveEdit(field: string) {
    const val = editValue.trim();
    if (field === 'voice_descriptors' || field === 'do_say' || field === 'dont_say' || field === 'signature_phrases') {
      await onSave({ [field]: val ? val.split(',').map(s => s.trim()).filter(Boolean) : [] });
    } else if (field === 'colors') {
      // Not inline-edited
    } else {
      await onSave({ [field]: val || null });
    }
    setEditing(null);
  }

  const fields: Array<{ key: keyof BrandProfile; label: string; display: string }> = [
    { key: 'brand_name', label: 'Brand Name', display: profile.brand_name || '—' },
    { key: 'one_liner', label: 'One-liner', display: profile.one_liner || '—' },
    { key: 'audience_description', label: 'Audience', display: profile.audience_description || '—' },
    { key: 'voice_descriptors', label: 'Voice', display: profile.voice_descriptors.join(', ') || '—' },
    { key: 'typical_cta_style', label: 'CTA Style', display: profile.typical_cta_style || '—' },
    { key: 'do_say', label: 'Do Say', display: profile.do_say.join(', ') || '—' },
    { key: 'dont_say', label: "Don't Say", display: profile.dont_say.join(', ') || '—' },
    { key: 'signature_phrases', label: 'Signature Phrases', display: profile.signature_phrases.join(', ') || '—' },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-sm">Brand Profile</h3>
        {hasIngestedAssets ? (
          <div className="text-right">
            <Button size="sm" variant="outline" onClick={onDistill} disabled={distilling} className="text-xs">
              {distilling ? <><Loader2 className="size-3 animate-spin mr-1" />Distilling…</> : <><Sparkles className="size-3 mr-1" />Re-distill</>}
            </Button>
            <p className="text-[10px] text-muted-foreground mt-1 leading-relaxed">
              AI analyzes your uploaded files and updates unlocked (🔓) fields. Locked (🔒) fields won't be changed.
            </p>
          </div>
        ) : null}
      </div>
      {/* Colors — editable */}
      <div className="flex items-start gap-3 group">
        <span className="text-xs text-muted-foreground w-20 shrink-0 pt-1">Colors</span>
        <div className="flex-1 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            {(['primary', 'accent'] as const).map(c => (
              <label key={c} className="flex items-center gap-1 cursor-pointer" title={`${c} color`}>
                <input
                  type="color"
                  value={profile.colors[c] || '#cccccc'}
                  onChange={async (e) => {
                    await onSave({
                      colors: { ...profile.colors, [c]: e.target.value },
                    });
                  }}
                  className="h-6 w-6 cursor-pointer rounded border border-border p-0"
                />
                <span className="text-[10px] font-mono text-muted-foreground">
                  {profile.colors[c] || `set ${c}`}
                </span>
              </label>
            ))}
          </div>
          <div className="flex flex-wrap gap-1">
            {COLOR_PALETTES.map(p => (
              <button
                key={p.name}
                type="button"
                title={p.name}
                onClick={async () => {
                  await onSave({
                    colors: { ...profile.colors, primary: p.primary, accent: p.accent },
                  });
                }}
                className={cn(
                  'flex items-center gap-0.5 rounded-md border px-1.5 py-1 transition',
                  profile.colors.primary === p.primary && profile.colors.accent === p.accent
                    ? 'border-primary ring-primary/20 ring-1'
                    : 'border-border hover:border-foreground/20',
                )}
              >
                <div className="size-3 rounded-full" style={{ background: p.primary }} />
                <div className="size-3 rounded-full" style={{ background: p.accent }} />
              </button>
            ))}
          </div>
        </div>
      </div>
      {/* Fields */}
      {fields.map(f => (
        <div key={f.key} className="flex items-start gap-3 group">
          <span className="text-xs text-muted-foreground w-20 shrink-0 pt-0.5">{f.label}</span>
          {editing === f.key ? (
            <div className="flex-1 flex gap-1">
              <Input value={editValue} onChange={e => setEditValue(e.target.value)} className="text-xs h-7" autoFocus onKeyDown={e => e.key === 'Enter' && saveEdit(f.key)} />
              <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => saveEdit(f.key)}>✓</Button>
              <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setEditing(null)}>✕</Button>
            </div>
          ) : (
            <button type="button" onClick={() => startEdit(f.key, f.display === '—' ? '' : f.display)} className="flex-1 text-left text-xs text-foreground hover:text-primary transition truncate">
              {f.display}
            </button>
          )}
          <button type="button" title={locked.has(f.key) ? 'Locked — won\'t be overwritten by distillation' : 'Unlocked — may be updated by distillation'}
            onClick={async () => {
              const newOverrides = locked.has(f.key)
                ? profile.manual_overrides.filter(o => o !== f.key)
                : [...profile.manual_overrides, f.key];
              await onSave({ manual_overrides: newOverrides } as any);
            }}
            className="opacity-0 group-hover:opacity-100 transition-opacity pt-0.5">
            {locked.has(f.key) ? <Lock className="size-3 text-primary" /> : <Unlock className="size-3 text-muted-foreground" />}
          </button>
        </div>
      ))}
      {profile.source && (
        <p className="text-[10px] text-muted-foreground">
          Source: {profile.source}{profile.last_distilled_at ? ` · Last distilled ${new Date(profile.last_distilled_at).toLocaleDateString()}` : ''}
        </p>
      )}
    </div>
  );
}

// ── Main page ───────────────────────────────────────────────────────────────
export function BrandPage() {
  const [profile, setProfile] = useState<BrandProfile | null>(null);
  const [assets, setAssets] = useState<BrandAsset[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<'loading' | 'empty' | 'questionnaire' | 'dashboard'>('loading');
  const [uploading, setUploading] = useState(false);
  const [distilling, setDistilling] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = useCallback(async () => {
    try {
      const [p, a] = await Promise.all([fetchBrandProfile(), fetchBrandAssets()]);
      setProfile(p);
      setAssets(a);
      setMode(p || a.length > 0 ? 'dashboard' : 'empty');
    } catch {
      // No brand data or tables not yet migrated — show setup page
      setMode('empty');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Poll while any asset is processing
  useEffect(() => {
    const hasProcessing = assets.some(a => a.status === 'uploaded' || a.status === 'processing');
    if (hasProcessing && !pollRef.current) {
      pollRef.current = setInterval(async () => {
        const a = await fetchBrandAssets().catch(() => []);
        if (a.length) setAssets(a);
      }, 3000);
    } else if (!hasProcessing && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [assets]);

  async function handleUpload(file: File, type: BrandAssetType) {
    setUploading(true); setError(null);
    try {
      await uploadBrandAsset(file, type);
      const a = await fetchBrandAssets();
      setAssets(a);
      setMode('dashboard');
    } catch (e) { setError(e instanceof Error ? e.message : 'Upload failed'); }
    setUploading(false);
  }

  async function handleDelete(id: string) {
    try { await deleteBrandAsset(id); setAssets(prev => prev.filter(a => a.id !== id)); } catch { setError('Delete failed'); }
  }

  async function handleReprocess(id: string) {
    try { await reprocessBrandAsset(id); await load(); } catch { setError('Reprocess failed'); }
  }

  async function handleSaveProfile(patches: Partial<BrandProfile>) {
    try { const p = await patchBrandProfile(patches); setProfile(p); } catch (e) { setError(e instanceof Error ? e.message : 'Save failed'); }
  }

  async function handleDistill() {
    setDistilling(true); setError(null);
    try { const p = await triggerDistill(); setProfile(p); } catch (e) { setError(e instanceof Error ? e.message : 'Distillation failed'); }
    setDistilling(false);
  }

  return (
    <div className="bg-background min-h-screen">
      <AppHeader currentTab="brand" />
      <main className="mx-auto max-w-[1200px] px-6 py-8 lg:px-10">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <Palette className="size-6 text-primary" />Brand
          </h1>
          <p className="text-muted-foreground text-sm mt-1">Help SendFast learn your brand so every generation sounds like you, not generic AI.</p>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription className="flex items-center justify-between">
              <span>{error}</span>
              <Button size="sm" variant="ghost" onClick={() => setError(null)} className="h-5 w-5 p-0"><X className="size-3" /></Button>
            </AlertDescription>
          </Alert>
        )}

        {mode === 'loading' && (
          <div className="flex items-center justify-center py-20 gap-2 text-muted-foreground text-sm">
            <Loader2 className="size-4 animate-spin" />Loading…
          </div>
        )}

        {mode === 'empty' && (
          <BrandSetupChoice
            onUpload={() => setMode('dashboard')}
            onQuestionnaire={() => setMode('questionnaire')}
          />
        )}

        {mode === 'questionnaire' && (
          <BrandQuestionnaire onComplete={p => { setProfile(p); setMode('dashboard'); }} />
        )}

        {mode === 'dashboard' && (
          <div className="grid gap-6 lg:grid-cols-2">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <Sparkles className="size-4 text-primary" />Profile
                </CardTitle>
              </CardHeader>
              <CardContent>
                {profile ? (
                  <ProfileEditor profile={profile} onSave={handleSaveProfile} onDistill={handleDistill} distilling={distilling} hasIngestedAssets={assets.some(a => a.status === 'ingested' && a.extracted_text)} />
                ) : (
                  <div className="space-y-3 text-center py-4">
                    <p className="text-muted-foreground text-sm">No profile yet.</p>
                    <Button size="sm" variant="outline" onClick={() => setMode('questionnaire')}>
                      <FileText className="size-3 mr-1" />Answer Questions
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <Upload className="size-4 text-primary" />Files
                </CardTitle>
              </CardHeader>
              <CardContent>
                <FileManager
                  assets={assets}
                  onUpload={handleUpload}
                  onDelete={handleDelete}
                  onReprocess={handleReprocess}
                  uploading={uploading}
                />
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
}
